import { useGetFsmPayoutRequestDetail } from "@fp/admin-api-client/payout-requests/payout-requests.client";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AddressLink, AddressMap } from "@fp/ui/components/common/address";
import {
  formatLocalDate,
  LocalDateTime,
} from "@fp/ui/components/common/local-date-time";
import { cx } from "@fp/ui/utils/cx";
import { User01 } from "@untitledui/icons";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { InvoiceExportButton } from "@/components/invoice-export-button";
import { invoiceStatusLabels } from "@/lib/invoice-utils";
import { PayoutQueueNav } from "@/components/payout-queue-nav";
import { PayoutRequestActions } from "@/components/payout-request-actions";
import {
  PayoutShortcutsHelp,
  payoutRequestShortcuts,
} from "@/components/payout-shortcuts-help";
import { FsmSourceTag } from "@/components/payout-source";
import { usePayoutReviewEscape } from "@/hooks/usePayoutReviewEscape";
import { isOverlayOpen } from "@/lib/is-overlay-open";
import { usePayoutReviewQueue } from "@/lib/payout-review-queue";

export const Route = createFileRoute("/payout-requests/fsm/$id/")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

function PayoutRequestStatusBadge({ status }: { status: string }) {
  const getStatusStyles = (status: string) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Approved":
        return "bg-blue-100 text-blue-800";
      case "Returned":
        return "bg-orange-100 text-orange-800";
      case "Denied":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
        getStatusStyles(status),
      )}
    >
      {status}
    </span>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetFsmPayoutRequestDetail(id);
  const fsmPayoutRequest = data?.data;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const {
    advanceToNext,
    goToNext,
    goToPrevious,
    returnToList,
    hasPrevious,
    hasNext,
  } = usePayoutReviewQueue({ payoutRequestId: id });

  const handleReviewed = () => {
    queryClient.invalidateQueries({ queryKey: ["payout-requests"] });
    advanceToNext();
  };

  useHotkeys(
    ["j", "down"],
    () => {
      if (!isOverlayOpen()) {
        goToNext();
      }
    },
    [goToNext],
  );
  useHotkeys(
    ["k", "up"],
    () => {
      if (!isOverlayOpen()) {
        goToPrevious();
      }
    },
    [goToPrevious],
  );
  usePayoutReviewEscape(returnToList);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!fsmPayoutRequest) {
    return (
      <AdminLayout>
        <AdminPageHeader
          title="Payout Request"
          parent={{ url: "/payout-requests", label: "Pay" }}
        />
        <PageContentContainer>
          <div className="bg-white text-center py-8 text-gray-500">
            Payout request not found
          </div>
        </PageContentContainer>
      </AdminLayout>
    );
  }

  const payoutRequest = fsmPayoutRequest.payoutRequest;
  const fsm = fsmPayoutRequest.fsm;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Payout Request"
        parent={{ url: "/payout-requests", label: "Pay" }}
        titleActions={
          <>
            <PayoutQueueNav
              onPrevious={goToPrevious}
              onNext={goToNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
            />
            <PayoutShortcutsHelp shortcuts={payoutRequestShortcuts(payoutRequest.canBeReturned)} />
          </>
        }
      />
      <PageContentContainer className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-row items-start justify-between mb-1">
            <div className="flex items-center gap-2 flex-1 min-w-0 pr-4">
              <User01 className="h-4 w-4 text-gray-400 shrink-0" />
              <h2 className="text-base font-semibold text-brand-600 truncate">
                {payoutRequest.payeeName}
              </h2>
            </div>
            {payoutRequest.amount != null && (
              <div className="text-base font-semibold text-brand-600 whitespace-nowrap">
                {formatCurrency(payoutRequest.amount)}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-row items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <FsmSourceTag
                platform={fsm.platform}
                referenceType={fsm.referenceType}
              />
              <PayoutRequestStatusBadge status={payoutRequest.status} />
              {payoutRequest.invoice && (
                <span className="text-xs text-gray-500">Invoice {payoutRequest.invoice.number} · {invoiceStatusLabels[payoutRequest.invoice.status]}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <InvoiceExportButton invoice={payoutRequest.invoice} />
              <div className="text-xs">
                {formatLocalDate(payoutRequest.createdDate, "dateWithYear")}
              </div>
            </div>
          </div>

          {payoutRequest.reviewNotes && (
            <div className="mt-6">
              <div className="text-sm font-semibold text-gray-700 mb-1">
                Review notes
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {payoutRequest.reviewNotes}
              </p>
            </div>
          )}

          {payoutRequest.reviewerName && payoutRequest.reviewedDate && (
            <div className="mt-6 text-sm text-gray-500">
              Reviewed by {payoutRequest.reviewerName} on{" "}
              <LocalDateTime value={payoutRequest.reviewedDate} format="full" />
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Source</h2>
          <dl className="space-y-4 text-sm">
            {fsm.customerName && (
              <div>
                <dt className="font-medium text-gray-900">Customer</dt>
                <dd className="mt-0.5 text-gray-600">{fsm.customerName}</dd>
              </div>
            )}
            {fsm.sourceReference && (
              <div>
                <dt className="font-medium text-gray-900">Reference</dt>
                <dd className="mt-0.5 text-gray-600">{fsm.sourceReference}</dd>
              </div>
            )}
            {fsm.itemAmount != null && (
              <div>
                <dt className="font-medium text-gray-900">Invoice amount</dt>
                <dd className="mt-0.5 text-gray-600">
                  {formatCurrency(fsm.itemAmount)}
                </dd>
              </div>
            )}
            {fsm.description && (
              <div>
                <dt className="font-medium text-gray-900">Service</dt>
                <dd className="mt-0.5 text-gray-600">
                  {fsm.description}
                </dd>
              </div>
            )}
            {fsm.notes && (
              <div>
                <dt className="font-medium text-gray-900">Work notes</dt>
                <dd className="mt-0.5 text-gray-600 whitespace-pre-wrap">
                  {fsm.notes}
                </dd>
              </div>
            )}
            {fsm.techEmail && (
              <div>
                <dt className="font-medium text-gray-900">Technician</dt>
                <dd className="mt-0.5 text-gray-600 break-all">
                  {fsm.techEmail}
                </dd>
              </div>
            )}
          </dl>
          {fsm.address && (
            <div className="flex flex-col gap-1 mt-4">
              <AddressMap address={fsm.address} />
              <AddressLink address={fsm.address} />
            </div>
          )}
        </div>
      </PageContentContainer>

      <PayoutRequestActions
        payoutRequestId={payoutRequest.id}
        name={payoutRequest.payeeName}
        amount={payoutRequest.amount ?? null}
        status={payoutRequest.status}
        concurrencyToken={payoutRequest.concurrencyToken}
        canBeReturned={payoutRequest.canBeReturned}
        onSuccess={handleReviewed}
        isDrawerOpen={isDrawerOpen}
        onDrawerOpenChange={setIsDrawerOpen}
      />
    </AdminLayout>
  );
}
